import * as React from 'react';

export interface ButtonProps {
    /**
     * Whether the button should be disabled
     */
    disabled: boolean;
    /**
     * Icon class to display
     */
    iconClass?: string;
    /**
     * Space delimited list of classes
     */
    className?: string;
    /**
     * Click handler
     */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /**
     * Button's name
     */
    name?: string;
    /**
     * Button's style
     */
    style?: React.CSSProperties;
    /**
     * Button's ID
     */
    id?: string;
}

export interface ButtonState {
    /**
     * The CSS classes on the button
     */
    cssClasses: string[];
    /**
     * Whether the button is disabled
     */
    disabled: boolean;
}

/**
 * This is a button component that mimics Jquery UI buttons
 */
export class JQueryUiButton extends React.Component<ButtonProps, ButtonState> {
    constructor(props: ButtonProps) {
        super(props);

        const className = props.className ? props.className : '';
        const cssClasses = this.getCssClasses(
            props.disabled,
            className.split(' ')
        );

        this.state = {
            cssClasses,
            disabled: props.disabled
        };
    }

    /**
     * Update the state when this component receives new props (a parent component was re-rendered)
     * @param nextProps New props
     */
    componentWillReceiveProps(nextProps: ButtonProps) {
        const disabled = nextProps.disabled;
        const className = nextProps.className ? nextProps.className : '';

        const cssClasses = this.getCssClasses(disabled, className.split(' '));

        this.setState({ disabled, cssClasses });
    }

    render() {
        const ariaDisabled = this.state.disabled;

        return (
            <button
                id={this.props.id}
                type="button"
                role="button"
                aria-disabled={ariaDisabled}
                className={this.state.cssClasses.join(' ')}
                onMouseEnter={() => this.handleMouseEnter()}
                onMouseLeave={() => this.handleMouseLeave()}
                onClick={event => {
                    this.handleClick(event);
                }}
                name={this.props.name ? this.props.name : ''}
                style={this.props.style}
            >
                <span className="ui-button-text">
                    {this.props.iconClass ? (
                        <span
                            className={
                                'ui-icon inline-icon ' + this.props.iconClass
                            }
                        />
                    ) : (
                        // props.children represents the inner contents of this component in the render
                        // function
                        // i.e. <JqueryUiButton>Contents</JqueryUiButton>
                        <span>{this.props.children}</span>
                    )}
                </span>
            </button>
        );
    }

    /**
     * Get the classes to add to the button
     * @param disabled Whether the button is disabled
     * @param additionalClasses Additional classes to add
     */
    private getCssClasses(disabled: boolean, additionalClasses?: string[]) {
        /**
         * The base classes
         */
        const cssClasses = [
            'ui-button',
            'ui-widget',
            'ui-state-default',
            'ui-corner-all',
            'ui-button-text-only'
        ];

        if (disabled) {
            cssClasses.push('ui-state-disabled');
            cssClasses.push('ui-button-disabled');
        }

        if (additionalClasses) {
            return [...cssClasses, ...additionalClasses];
        } else {
            return cssClasses;
        }
    }

    /**
     * Handles the mouse entering the button
     */
    private handleMouseEnter() {
        if (this.props.disabled) {
            return;
        }

        const cssClasses = [...this.state.cssClasses];

        cssClasses.push('ui-state-hover');

        this.setState({ cssClasses });
    }

    /**
     * Handles the mouse leaving the button
     */
    private handleMouseLeave() {
        if (this.props.disabled) {
            return;
        }

        const cssClasses = [...this.state.cssClasses].filter(
            (value: string) => value !== 'ui-state-hover'
        );

        this.setState({ cssClasses });
    }

    /**
     * Handles the button being clicked
     * @param event The click event
     */
    private handleClick(event: React.MouseEvent<HTMLButtonElement>) {
        if (!this.state.disabled && this.props.onClick) {
            this.props.onClick(event);
        }
    }
}
